import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { SubmissionStatus } from 'src/app/enum/submission.status';
import { SelectOption } from 'src/app/model/select.option';
import { groupFields, dictControlFields, dictControlFilters, publicationFields, specialColumnsFields, fieldDependencies } from 'src/app/model/submissionFieldFilter';
import { SubmissionsFilter } from 'src/app/enum/submissions.filter';
import { AdminService } from 'src/app/service/admin.service';
import { EventsService } from 'src/app/service/events.service';
import { Event } from 'src/app/model/event';
import { EventTopic } from 'src/app/model/eventTopic';

@Component({
  selector: 'app-submissions-field-filter-v2',
  templateUrl: './submissions-field-filter-v2.component.html',
  styleUrls: ['./submissions-field-filter-v2.component.scss']
})
export class SubmissionsFieldFilterV2Component implements OnInit {
  @Input() readonly page: 'ranking' | 'publication' = 'ranking';

  // Qual parte do conteúdo renderizar: 'fields' = seletores de coluna (checkboxes),
  // 'filters' = filtros de verdade (dropdowns), 'all' = ambos (padrão). Permite
  // separar colunas (menu "...") e filtros (menu de filtro) na ranking.
  @Input() section: 'all' | 'fields' | 'filters' = 'all';

  // Habilita os botões "Atualizar lista"/"Descartar" do menu. O pai
  // (submission-ranking) passa o estado "dirty" (houve alteração nos filtros desde
  // que o menu abriu).
  @Input() actionsEnabled: boolean = true;

  public readonly SubmissionsFilter = SubmissionsFilter;
  @Input() public readonly event: Event;
  @Input() formFields: FormControl;
  @Input() formFilters: FormGroup;

  // Habilita a opção "ícone de similaridade" (sub-campo de "Arquivos") no
  // menu — só faz sentido mostrar a opção quando o evento tem a verificação
  // de similaridade (Docoloc) habilitada na config de revisão.
  @Input() similarityVerificationEnabled: boolean = false;

  @Output() showSubmissionsEvent = new EventEmitter<boolean>();

  // Menu de filtros modal (bare): "Descartar" — o pai restaura o snapshot do form
  // e fecha o menu. Aqui só sinalizamos a intenção.
  @Output() discardEvent = new EventEmitter<void>();

  private groupFields = groupFields;

  // Options do filtro de status enriquecidas para o modo CHIP do app-select-input:
  // além de id/value (valor do form), carregam statusClass (cor do chip), icon (glyph
  // Material) e label (chave i18n traduzida). Tipado any[] pois SelectOption é fixo.
  public status: any[] = SubmissionStatus.toIterable().map(value => ({
    id: value,
    value: value,
    statusClass: SubmissionStatus.toClass(value as SubmissionStatus),
    icon: SubmissionStatus.icon(value as SubmissionStatus).fontIcon,
    label: SubmissionStatus.toString(value as SubmissionStatus),
  }));
  public topics: SelectOption[] = [];
  public tracks: SelectOption[] = [];
  public tpcGroups: SelectOption[] = [];
  @Input() readonly groups: SelectOption[] = []

  constructor(
    private adminService: AdminService,
    private eventService: EventsService
  ) { }

  ngOnInit(): void {
    this.enforceFieldDependencies();
    this.buildTopicOptions();
    this.buildTrackOptions();
    this.getTpcGroupOptions();
  }

  public changeFields(control: string) {
    const formFieldsValue = this.formFields.value;
    if (formFieldsValue.includes(control)) {
      if (this.isSpecialFieldsColumn(control)) {
        this.removeSpecialColumnsField(control, formFieldsValue);
      }

      formFieldsValue.splice(formFieldsValue.indexOf(control), 1);

      // Ao desmarcar um pai, desmarca os filhos que dependem dele — eles deixam
      // de fazer sentido (ex.: "Nome do revisor" sem "Notas individuais").
      this.removeDependentFields(control, formFieldsValue);
    } else {
      if (this.isSpecialFieldsColumn(control)) {
        this.addSpecialColumnsField(control, formFieldsValue);
      }

      formFieldsValue.push(control);
    }
    this.formFields.setValue(formFieldsValue);

    // Menu de colunas (section='fields'): aplica e persiste na hora, sem botão.
    if (this.section === 'fields') {
      this.showSubmissions();
    }
  }

  public isChecked(control: string) {
    return this.formFields.value.includes(control);
  }

  // Nº de opções marcadas num select de filtro multi (badge no canto da caixa).
  // `.get(name).value` (não o `formFilters.value`) devolve o array MESMO com o
  // controle desabilitado; select vazio/desabilitado → 0 → badge escondido.
  public selectedCount(name: string): number {
    const v = this.formFilters?.get(name)?.value;
    return Array.isArray(v) ? v.length : 0;
  }

  // Filtros de verdade (multi-selects) — NÃO inclui COLOR_SCHEME (modo de exibição).
  private readonly FILTER_CONTROLS = [
    SubmissionsFilter.STATUS,
    SubmissionsFilter.TOPICS,
    SubmissionsFilter.TRACKS,
    SubmissionsFilter.SUBMISSION_GROUPS,
    SubmissionsFilter.TPC_GROUPS,
  ];

  // Há ao menos um filtro marcado? (habilita/mostra o "Limpar filtros").
  public get hasAnyFilter(): boolean {
    return this.FILTER_CONTROLS.some(name => this.selectedCount(name) > 0);
  }

  // "Limpar filtros": zera TODOS os multi-selects (deixa as caixas vazias), sem
  // aplicar — o usuário confirma com "Atualizar" (mesma semântica modal do painel).
  // Não mexe no COLOR_SCHEME. Marca o form dirty p/ habilitar o "Atualizar".
  public clearFilters(): void {
    this.FILTER_CONTROLS.forEach(name => this.formFilters?.get(name)?.setValue([]));
  }

  // Um campo só pode ser ativado se sua dependência (pai) estiver marcada.
  // Ex.: "Nome do revisor" depende de "Notas individuais" (score-numeric).
  public isFieldEnabled(control: string): boolean {
    const parent = fieldDependencies[control];
    return !parent || this.isChecked(parent);
  }

  // Campo dependente (tem um pai) — renderizado indentado sob o pai.
  public isDependentField(control: string): boolean {
    return !!fieldDependencies[control];
  }

  // Alguns campos só devem aparecer no menu sob uma condição extra além da
  // dependência pai/filho (ex.: o evento precisa ter uma config habilitada).
  // Hoje só o ícone de similaridade usa isso; os demais campos são sempre
  // visíveis (a visibilidade "normal" de pai/filho já é tratada por
  // isFieldEnabled, que apenas desabilita — não some do menu).
  public isFieldVisible(control: string): boolean {
    if (control === dictControlFields['filesSimilarityIcon']) {
      return this.similarityVerificationEnabled;
    }
    return true;
  }

  // Remove do form todos os campos cujo pai-dependência seja `control`.
  private removeDependentFields(control: string, formFieldsValue: string[]): void {
    Object.keys(fieldDependencies).forEach(child => {
      if (fieldDependencies[child] === control) {
        const i = formFieldsValue.indexOf(child);
        if (i !== -1) formFieldsValue.splice(i, 1);
      }
    });
  }

  // Reconciliação inicial: descarta filhos cuja dependência não está satisfeita
  // (estados salvos antigos, anteriores a esta regra, podiam ter o filho ativo
  // sem o pai). Mantém o form coerente para a tabela renderizar sem incoerência.
  private enforceFieldDependencies(): void {
    const values: string[] = this.formFields?.value;
    if (!values) return;
    let changed = false;
    Object.keys(fieldDependencies).forEach(child => {
      if (values.includes(child) && !values.includes(fieldDependencies[child])) {
        values.splice(values.indexOf(child), 1);
        changed = true;
      }
    });
    if (changed) this.formFields.setValue(values);
  }

  private buildTopicOptions() {
    // O que aparece é decidido pela VISIBILIDADE de cada tópico (`active`), não
    // pelo número de filhos: mostrar só raízes ativas e, dentro delas, só os
    // subtópicos ativos. Raiz ativa com subtópicos ativos vira grupo; raiz ativa
    // sem subtópicos ativos (tópico plano/visível) vira opção direta.
    this.topics = (this.event.topics || [])
      .filter((t: EventTopic) => !t.isSubtopic && t.active)
      .map((t: EventTopic) => {
        const activeChildren = (t.children || []).filter((c: EventTopic) => c.active);
        return activeChildren.length > 0
          ? new SelectOption(t.id, t.name, null, activeChildren.map(c => new SelectOption(c.id, c.name)))
          : new SelectOption(t.id, t.name);
      });
  }

  private buildTrackOptions() {
    this.tracks = this.event.tracks.map(track => new SelectOption(track.id, track.name));
  }

  private getTpcGroupOptions(): void {
    this.eventService.getEventTPCGroups(this.event.id, 'minimum')
      .subscribe(tpcGroups => this.tpcGroups = tpcGroups.map(group => new SelectOption(group.id, group.name)));
  }

  public showSubmissions() {
    this.adminService.progressBar.start();
    this.showSubmissionsEvent.emit(true);
    this.submitForms();
  }

  public discard() {
    this.discardEvent.emit();
  }

  // Grupos do seletor de colunas referenciados por `control`, para o layout
  // organizado do menu bare:
  //   [Submissão] [Autores/Evento] [Revisões e notas] [Outros/Controles de publicação]
  // `publicationGroup` pode não existir (é adicionado em runtime pela tabela só se
  // o evento tiver controles de publicação) — o template trata com *ngIf.
  public get submissionGroup() { return this.findGroup('submission'); }
  public get authorGroup() { return this.findGroup('author'); }
  public get eventGroup() { return this.findGroup('event'); }
  public get scoreGroup() { return this.findGroup('score'); }
  public get othersGroup() { return this.findGroup('others'); }
  public get publicationGroup() { return this.findGroup('publication'); }

  private findGroup(control: string) {
    return this.groupFields.find(group => group.control === control);
  }

  private submitForms(): void {
    const formsSubmit = {...this.listFieldsObj, ...this.customFieldsObj, ...this.listFiltersObj, page: this.page};

    this.eventService.putSubmissionFieldsAndFilters(this.event.id, formsSubmit).subscribe(() => {});
  }

  private get listFiltersObj() {
    const filtersSubmit = {};
    for (const filter of Object.keys(this.formFilters.value)) {
      if (filter === SubmissionsFilter.COLOR_SCHEME) continue;
      
      const key = Object.keys(dictControlFilters).find(k => dictControlFilters[k] === filter);

      const value = this.formFilters.value[filter]?.join(',');
      if (key) filtersSubmit[key] = value;
    }

    return filtersSubmit;
  }

  private get listFieldsObj() {
    const fieldsSubmit = {};
    for (const field of this.formFields.value) {
      const key = Object.keys(dictControlFields).find(k => dictControlFields[k] === field);

      if (key) fieldsSubmit[key] = true;
    }

    Object.keys(dictControlFields).forEach(key => {
      if (!fieldsSubmit[key]) fieldsSubmit[key] = false;
    });

    return fieldsSubmit;
  }

  private get customFieldsObj() {
    const customFieldsSubmit = { customFields: '' };

    for (const field of this.formFields.value) {
      if (field.startsWith(publicationFields.customFields)) {
        const id = field.split('-')[1] + ','; // Just the id of the custom field is necessary to submit to the back-end
        customFieldsSubmit.customFields = `${customFieldsSubmit.customFields}${id}`;
      }
    }

    return customFieldsSubmit;
  }

  private isSpecialFieldsColumn(control: string): boolean {
    return Object.keys(specialColumnsFields).includes(control);
  }

  private addSpecialColumnsField(control: string, formsFieldsValue: string[]): void {
    Object.keys(specialColumnsFields).forEach(key => {
      if (key === control) {
        formsFieldsValue.push(...specialColumnsFields[key]);
      }
    });
  }

  private removeSpecialColumnsField(control: string, formsFieldsValue: string[]): void {
    Object.keys(specialColumnsFields).forEach(key => {
      if (key === control) {
        specialColumnsFields[key].forEach(field => {
          formsFieldsValue.splice(formsFieldsValue.indexOf(field), 1);
        });
      }
    });
  }
}
